import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { MoldService } from '../../../service/api/mold-service';
import { PartService } from '../../../service/api/part-service';

const { FUTUREACTIONS_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'futureactions';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    fromDate: null,
    toDate: null,
    isReleased: 0,
  },
  futureActionsLoading: false,
  filterVisible: false,
  tileView: false,
  selectedFutureAction: null,
  editModalVisible: false,
  futureActionsRecordsData: [],
};

export const fetchFutureActions = createAsyncThunk(
  'futureactions/fetchFutureActions',
  async (filters, { rejectWithValue }) => {
    try {
      console.log('🚀 Future Actions: Calling getAllFutureActions/1 API');
      console.log('🚀 Future Actions: MoldService method exists:', typeof MoldService.getAllFutureActions);
      const response = await MoldService.getAllFutureActions(1);
      console.log('✅ Future Actions API response:', response);
      console.log('✅ Future Actions API response data:', response.response);
      if (response.status === 200) {
        return response.response;
      } else {
        console.error('❌ Future Actions API failed with status:', response.status);
        return rejectWithValue(response.errorMessage?.message || 'Failed to load future actions');
      }
    } catch (error) {
      console.error('❌ Future Actions API error:', error);
      return rejectWithValue(error.message || 'Failed to load future actions');
    }
  },
);

export const fetchFutureActionsByStatus = createAsyncThunk(
  'futureactions/fetchFutureActionsByStatus',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await MoldService.getAllFutureActions(1);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load future actions');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load future actions');
    }
  },
);

export const addFutureAction = createAsyncThunk(
  'futureactions/addFutureAction',
  async (futureActionPayload, { rejectWithValue }) => {
    try {
      const response = await MoldService.saveUpdateReceiving(futureActionPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add future action');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add future action');
    }
  },
);

export const editFutureAction = createAsyncThunk(
  'futureactions/editFutureAction',
  async (futureActionPayload, { rejectWithValue }) => {
    try {
      const response = await MoldService.saveUpdateReceiving(futureActionPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update future action');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update future action');
    }
  },
);

export const editFutureActionData = createAsyncThunk(
  'futureactions/editFutureActionData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await MoldService.getMoldByToolIdWithCategory(id, 'Future-Actions');
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load future action data',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load future action data');
    }
  },
);

export const fetchFutureActionsWithFilters = () => (dispatch, getState) => {
  const filters = getState().futureactions.filters;
  dispatch(
    fetchFutureActions({
      searchText: filters.searchText,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
    }),
  );
};

export const fetchFutureActionsWithStatus = () => (dispatch, getState) => {
  const filters = getState().futureactions.filters;
  dispatch(
    fetchFutureActionsByStatus({
      isReleased: filters?.isReleased
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'futureactions',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setFromDate: (state, action) => {
      state.filters.fromDate = action.payload;
    },
    setToDate: (state, action) => {
      state.filters.toDate = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.isReleased = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFutureActions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFutureActions.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFutureActions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchFutureActionsByStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFutureActionsByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFutureActionsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addFutureAction.fulfilled, (state, action) => {
        const newFutureAction = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newFutureAction, ...state.data];
      })
      .addCase(addFutureAction.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editFutureAction.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editFutureAction.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editFutureActionData.pending, state => {
        state.receiveLoading = true;
      })
      .addCase(editFutureActionData.fulfilled, state => {
        state.receiveLoading = false;
      })
      .addCase(editFutureActionData.rejected, state => {
        state.receiveLoading = false;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addFutureAction.rejected, editFutureAction.rejected, editFutureActionData.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addFutureAction.fulfilled, editFutureAction.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(FUTUREACTIONS_ADD_EDIT));
    listenerApi.dispatch(fetchFutureActionsWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Future Action saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editFutureActionData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(FUTUREACTIONS_ADD_EDIT, action.payload));
  },
});

redux.injectReducer(STORE_KEY, reducer);
